export default {
  settings: {
    disableIframePostMessage: false,
  },

  lastUpdated: 'September 2026',
  revision: '3',
  analytics: {
    // gtmcode:"GTM-TJBC8K3M",
    gtmcode:"GTM-NCL4Z8",
    gacode: "G-KQG4N84HK1"
  },

  content: {
    title: "BUSSQ | Hitting the target",
    // BUSSQ is on legacy SuperModeller v2 URL parameter names.
    supermodellerVersion: "v2",
    supermodellerLink: "https://bussq.gbsau.com/supermodeller/",
    welcome: {
      logoUrl: "BUSSQ_RGB_Logo_With_Tagline_Positive.png",
      content: `
        <h2 class="lt-welcome-title" tabindex="0">Hitting the target</h2>
        <p>Build a picture of your lifestyle in retirement by taking this quick quiz.</p>
        <p>Then, find out how much money you’d need each year to achieve it.</p>`,
      buttonLabel: "Start building your future",
    },

    results: {
      supermodellerName: "retirement projection calculator",
      helpText: `<p class="m-0">The AFSA retirement standard calculates the annual expenditure in today’s dollars to maintain a basic, modest and comfortable lifestyle in retirement. The amount assumes that the retiree(s) own their own home outright and are relatively healthy. Learn more about the <a href='https://www.superannuation.asn.au/resources/retirement-standard' target='_blank' >ASFA Retirement standard and underlying assumptions.</a></p>`,
      CTA1: `
      <h3>Get advice</h3>
      <p>Personalise your plan further to see what is possible in retirement. Talk to a BUSSQ Financial Advisor to help you plan for your future. <a href='https://www.bussq.com.au/s/Tools-and-advice/Your-advice-options' target='_blank'>Find out more.</a></p>
      `,
      CTA2: `
      <h3>Attend a seminar</h3>
      <p>BUSSQ offers seminars to give you the tools to start building a better retirement. <a href='https://www.bussq.com.au/s/Tools-and-advice/Your-advice-options-/Seminars-and-events' target='_blank'>Register now.</a></p>
      `,
      CTA3: `
      <h3>Start planning for retirement</h3>
      <p>Check out the resources available to help you start to plan for your retirement. <a href='https://www.bussq.com.au/s/Retirement/Understanding-retirement/Planning-retirement' target='_blank'>Learn more.</a></p>
      `,
    },

    standards: {
      minimum: {
        heading: "Age pension with a little bit extra",
        headingHousehold: "Household expenses",
        descriptionHousehold: "Very basic repairs, like leaky roofs or major plumbing problem",
        headingTravel: "Travel",
        descriptionTravel: "Occasional short break or day trip in your own city",
        headingFood: "Food",
        descriptionFood: "Only local club special meals or inexpensive take-away",
        headingClothes: "Clothes",
        descriptionClothes: "Very basic clothing and footwear budget"
      },
      modest: {
        heading: "Modest",
        headingHousehold: "Household expenses",
        descriptionHousehold: "Limited budget for home repairs and household appliances",
        headingTravel: "Travel",
        descriptionTravel: "Annual domestic trip or a few short breaks",
        headingFood: "Food",
        descriptionFood: "Limited meals out at inexpensive restaurants, infrequent home-delivery or take-away",
        headingClothes: "Clothes",
        descriptionClothes: "Limited budget to replace or update worn items"
      },
      comfortable: {
        heading: "Comfortable",
        headingHousehold: "Household expenses",
        descriptionHousehold: "Home repairs, updates and maintenance to kitchen and bathroom appliances",
        headingTravel: "Travel",
        descriptionTravel: "Annual domestic trip to visit family, one overseas trip every seven years",
        headingFood: "Food",
        descriptionFood: "Occasional restaurant meals, home-delivery meals, take-away coffee",
        headingClothes: "Clothes",
        descriptionClothes: "Replace worn-out clothing and footwear items, modest wardrobe updates"
      }
    },

    disclaimer: {
      content: `
      <h4>Disclaimer</h4>
      <p>This calculator provides an illustration of your estimate retirement income. The totals are calculated based on the data you input and can only be as accurate as the information provided.</p>
      <p>Please remember that the results shown by the modeller are for illustrative purposes only and do not guarantee any future outcome or entitlement.</p>
      <p>You should also remember that you will need to pay income tax and the retirement target figure is based on your expenditure, not your pre-tax income.</p>
      <p>Any choices you make or data that you enter while using the calculator are erased once you have finished using the calculator. However, you have a responsibility to ensure that you exit the website when you have finished with the calculator, especially if you leave your computer for any length of time.</p>
      <h4>Assumptions</h4>
      <p>Your estimate retirement income calculation is based on research conducted by the Association of Superannuation Funds of Australia (ASFA). The research sets out three ‘Retirement Living Standards’ (Minimum, Moderate and Comfortable) which are based upon a basket of goods and services. You can find out more about the research and each of the Standards at <a href='https://www.superannuation.asn.au/resources/retirement-standard' target='_blank'>https://www.superannuation.asn.au/resources/retirement-standard</a></p>
      <p>Your estimated retirement income figure assumes you will have no rent or mortgage costs to pay in retirement.</p>
      <p>Your monthly savings figure is based on the following assumptions:</p>
      <ul>
        <li>If you selected that you live with a partner, this is your total ‘household’ savings amount.</li>
        <li>It will provide you with an estimated retirement income based on <a href='https://www.superannuation.asn.au/resources/retirement-standard' target='_blank'>ASFA’s Retirement Standards</a> Detailed Expenditure Breakdowns. The figures in each case assume that the retiree(s) own their own home and relate to expenditure by the household. This can be greater than household income after income tax where there is a drawdown on capital over the period of retirement.</li>
        <li>You (and your partner if applicable) will be entitled to the full age pension. For the purposes of the calculation, the calculator assumes your age pension will come into payment from your selected retirement age. To see when you’ll be eligible to receive the age pension visit: <a href='https://www.servicesaustralia.gov.au/who-can-get-age-pension?context=22526' target='_blank'>www.servicesaustralia.gov.au/who-can-get-age-pension</a>.</li>
        <li>All figures and current tax rates are in today’s terms to take into account the effect of inflation.</li>
      </ul>
      `,
    },

    assumptions: {
      content: ``,
    },

    gtag: {
      clickCTA1Description: `Get advice`,
      clickCTA2Description: `Attend a seminar`,
      clickCTA3Description: `Start planning for retirement`
    }
  },

  params: {
    ageNow: {
      min: 18,
      max: 74,
    },
    salary: {
      min: 100,
      max: 500000,
    },
    superannuationBalance: {
      min: 0,
      max: 3000000,
    },
    retirementAge: {
      min: 55,
      max: 74,
    },
  },
};
